// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import type { BreakerState } from '../../../../utils/powerFlowLive';
import { dashDelayAt } from './flowPulse';
import {
  BREAKER_ARC_R,
  CHEVRON_DEPTH,
  CHEVRON_HALF_WIDTH,
  INNER_ARMS,
  OPEN_SWING_DEG,
} from '../../../../utils/powerFlowBreakers';

/**
 * The drawout circuit breaker as the site's own engineering one-line draws
 * it: two nested chevron pairs pointing away from the device for the drawout
 * contacts, and the contact arc bowing off the run between two contact dots.
 * ANSI / IEEE 315 form, transcribed from the SGI-A sheet, proportions and all.
 *
 * Drawn around the origin on a vertical run, so it sits on a placement with
 * `translate(x y)`. The run between the contacts is *cut* by the caller (a
 * mask in `SldEdgeLayer`), never painted over, so the glyph reads the same on
 * any background, traced or not. Everything paints `currentColor`; the arc is
 * always an outline, as on the sheet.
 *
 * Position is the arc's **geometry**, never its fill: closed is the sheet's
 * own drawing, the arc seated on both contacts; open swings the arc about its
 * top contact, clear of the bottom one, the way a one-line draws an open
 * switch blade; unknown keeps the arc seated but dashed and faded, so no
 * reading passes for either. Alarm is a small warn triangle standing off the
 * arc where the sheet prints the rating — the shape `StatusMark` uses for warn.
 */
export const BreakerGlyph = ({
  position,
  alarm = false,
  flowing = false,
  flowEntry,
  flowReversed = false,
  horizontal = false,
  onAlarmClick,
}: {
  position: BreakerState['position'];
  alarm?: boolean;
  /** Makes the ALARM mark a button. Absent leaves it inert, as the rest of the layer is. */
  onAlarmClick?: () => void;
  /** The run is carrying load: its running dash continues along the arc. */
  flowing?: boolean;
  /**
   * Path distance from the conductor's start to the contact the dash enters
   * at (`breakerFlowEntry`). The arc's dash is delayed to match the run's
   * phase there, so the dash passes through the breaker without a jump.
   */
  flowEntry?: number;
  /** The run passes through the glyph against the arc's drawing direction. */
  flowReversed?: boolean;
  /**
   * On a horizontal run: the body turns a quarter so the contacts lie along
   * the run and the arc bows down, away from a bus above — the way the SGI-A
   * sheet draws its bus-tie breaker on the bus.
   */
  horizontal?: boolean;
}) => {
  const r = BREAKER_ARC_R;
  /** A chevron whose apex points away from the arc: up for dir -1, down for 1. */
  const chevron = (armsY: number, dir: 1 | -1) =>
    `M${-CHEVRON_HALF_WIDTH},${armsY} L0,${armsY + dir * CHEVRON_DEPTH} L${CHEVRON_HALF_WIDTH},${armsY}`;
  return (
    <g className={`sld-breaker__glyph sld-breaker__glyph--${position}`} fill="none">
      {/*
        The body takes its conductor's look — dotted when nothing flows. The
        alarm mark sits outside it: an alarm is a measured fact, never dotted.
      */}
      <g className="sld-breaker__body" transform={horizontal ? 'rotate(90)' : undefined}>
        {/* Drawout contacts: nested chevrons pointing away from the arc. */}
        <path className="sld-breaker__chevron" d={chevron(-INNER_ARMS, -1)} />
        <path className="sld-breaker__chevron" d={chevron(-INNER_ARMS - CHEVRON_DEPTH, -1)} />
        <path className="sld-breaker__chevron" d={chevron(INNER_ARMS, 1)} />
        <path className="sld-breaker__chevron" d={chevron(INNER_ARMS + CHEVRON_DEPTH, 1)} />
        {/* The contact arc, bowing right off the run; swung about the top contact when open. */}
        <path
          className="sld-breaker__arc"
          d={`M0,${-r} A${r},${r} 0 0 1 0,${r}`}
          transform={position === 'open' ? `rotate(${-OPEN_SWING_DEG} 0 ${-r})` : undefined}
        />
        <circle className="sld-breaker__contact" cx="0" cy={-r} r="1.5" />
        <circle className="sld-breaker__contact" cx="0" cy={r} r="1.5" />
        {/*
          The arc is πr long over a 2r cut, so the dash leaves the breaker a
          little ahead of where the run resumes; it enters in phase, which is
          what the eye follows.
        */}
        {flowing && (
          <path
            className="sld-breaker__flow"
            d={
              flowReversed ? `M0,${r} A${r},${r} 0 0 0 0,${-r}` : `M0,${-r} A${r},${r} 0 0 1 0,${r}`
            }
            style={
              flowEntry === undefined
                ? undefined
                : { animationDelay: `${dashDelayAt(flowEntry)}ms` }
            }
            data-sld-breaker-flow
          />
        )}
      </g>
      {/*
        The alarm mark says what it is: the warn triangle with its bang, and
        the word beside it. A bare triangle read as decoration on a sheet of
        five breakers in alarm.
      */}
      {alarm && (
        <g
          className={`sld-breaker__alarm${onAlarmClick ? ' is-clickable' : ''}`}
          // Upright either way: beside the arc on a vertical run, below it on a
          // horizontal one.
          transform={horizontal ? `translate(${-(r + 12)} ${r + 12})` : undefined}
          data-sld-breaker-alarm-mark
          // The conductor layer is pointer-transparent so the stage can pan
          // through it; this mark opts back in and keeps its press to itself,
          // or the press would also start a pan and the click clear the selection.
          role={onAlarmClick ? 'button' : undefined}
          tabIndex={onAlarmClick ? 0 : undefined}
          aria-label={onAlarmClick ? 'Show breaker alarm' : undefined}
          onPointerDown={onAlarmClick ? event => event.stopPropagation() : undefined}
          onClick={
            onAlarmClick
              ? event => {
                  event.stopPropagation();
                  onAlarmClick();
                }
              : undefined
          }
          onKeyDown={
            onAlarmClick
              ? event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    event.stopPropagation();
                    onAlarmClick();
                  }
                }
              : undefined
          }
        >
          <path
            className="sld-breaker__alarm-shape"
            d={`M${r + 12},${-5} L${r + 17.5},${4.5} L${r + 6.5},${4.5} Z`}
          />
          <path
            className="sld-breaker__alarm-bang"
            d={`M${r + 12},${-1.5} V${1.5} M${r + 12},${3} V${3.4}`}
          />
          <text className="sld-breaker__alarm-text" x={r + 20} y={3.2}>
            ALARM
          </text>
        </g>
      )}
    </g>
  );
};
